/**
 * Inventory › Lots (بهر و بچ).
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory list archetype (V2DataGrid) with
 * V2Modal forms; docs/design-system/V2_USAGE.md §4 and §6.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2ConfirmDialog,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Modal,
  V2Table,
  V2ToastStack,
  type V2GridColumn,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { faDigits } from '../../lib/faDigits.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  LOT_STATUS_META,
  formatQuantity,
  formatQuantityParts,
  isTruncated,
  metaOf,
  optionsOf,
  pickerOptions,
} from './inventoryDisplay.js';
import { useDebouncedValue, useToasts } from './useInventoryUi.js';
import './inventory.css';

interface LotListItem {
  readonly lot_id: string;
  readonly lot_code: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly manufacture_date: string | null;
  readonly expiry_date: string | null;
  readonly status: string;
  readonly supplier_reference: string | null;
  readonly receipt_evidence: string | null;
  readonly on_hand_qty_num: string;
  readonly on_hand_qty_den: string;
  readonly created_at: string;
}

interface LotDetailResponse {
  readonly lotId: string;
  readonly productId: string;
  readonly lotCode: string;
  readonly manufactureDate: string | null;
  readonly expiryDate: string | null;
  readonly supplierReference: string | null;
  readonly receiptEvidence: string | null;
  readonly status: string;
  readonly balances: readonly {
    readonly warehouseId: string;
    readonly warehouseLocationId: string;
    readonly onHand: { readonly numerator: string; readonly denominator: string };
    readonly reserved: { readonly numerator: string; readonly denominator: string };
    readonly blocked: { readonly numerator: string; readonly denominator: string };
  }[];
}

interface LotsResponse {
  readonly items: readonly LotListItem[];
  readonly total: number;
}

type BalanceRow = LotDetailResponse['balances'][number] & { readonly id: string };

const PICKER_LIMIT = 200;
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'همه' },
  ...optionsOf(LOT_STATUS_META, ['active', 'quarantined', 'released', 'expired', 'blocked']),
];
const NEW_LOT_STATUS_KEYS = ['active', 'quarantined'] as const;

export function LotsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newProductId, setNewProductId] = useState('');
  const [newLotCode, setNewLotCode] = useState('');
  const [newMfgDate, setNewMfgDate] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [newSupplierRef, setNewSupplierRef] = useState('');
  const [newEvidence, setNewEvidence] = useState('');
  const [newStatus, setNewStatus] = useState<string>('active');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Status dialog
  const [statusItem, setStatusItem] = useState<LotListItem | null>(null);
  const [targetStatus, setTargetStatus] = useState<string>('active');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Quarantine release dialog
  const [quarantineItem, setQuarantineItem] = useState<LotListItem | null>(null);
  const [quarantineTarget, setQuarantineTarget] = useState<'released' | 'blocked'>('released');
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [quarantineError, setQuarantineError] = useState<string | null>(null);

  // Auto-expiry confirm
  const [expireOpen, setExpireOpen] = useState(false);
  const [expiring, setExpiring] = useState(false);

  // Stock-by-warehouse dialog
  const [detailItem, setDetailItem] = useState<LotListItem | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const url = useMemo(() => {
    if (!slug) return null;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (productFilter) params.set('productId', productFilter);
    return `${base}/api/v1/inventory/lots?${params.toString()}`;
  }, [base, debouncedSearch, productFilter, slug, statusFilter]);

  const result = useApiGet<LotsResponse>(url, [url]);
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const detail = useApiGet<LotDetailResponse>(
    slug && detailItem ? `${base}/api/v1/inventory/lots/${detailItem.lot_id}` : null,
    [slug, detailItem?.lot_id],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug && detailItem ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug, detailItem?.lot_id],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug && detailItem ? `${base}/api/v1/master-data/locations?limit=${PICKER_LIMIT}` : null,
    [slug, detailItem?.lot_id],
  );

  const productOptions = useMemo(
    () => (products.status === 'ready' ? pickerOptions(products.data.items, 'productId') : []),
    [products],
  );
  const productsTruncated =
    products.status === 'ready' && isTruncated(products.data.items.length, products.data.total);

  const warehouseName = useMemo(() => {
    const names = new Map<string, string>();
    if (warehouses.status === 'ready') {
      for (const o of pickerOptions(warehouses.data.items, 'warehouseId', false))
        names.set(o.value, o.label);
    }
    return names;
  }, [warehouses]);
  const locationName = useMemo(() => {
    const names = new Map<string, string>();
    if (locations.status === 'ready') {
      for (const o of pickerOptions(locations.data.items, 'warehouseLocationId', false))
        names.set(o.value, o.label);
    }
    return names;
  }, [locations]);

  const resetCreate = () => {
    setNewProductId('');
    setNewLotCode('');
    setNewMfgDate('');
    setNewExpiryDate('');
    setNewSupplierRef('');
    setNewEvidence('');
    setNewStatus('active');
    setCreateError(null);
  };

  const handleCreateLot = async (e: FormEvent) => {
    e.preventDefault();
    if (creating) return;
    if (!newProductId || !newLotCode.trim()) {
      setCreateError('کالا و کد بهر را وارد کنید.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiPost(`${base}/api/v1/inventory/lots`, {
        productId: newProductId,
        lotCode: newLotCode.trim(),
        expiryDate: newExpiryDate || undefined,
        manufactureDate: newMfgDate || undefined,
        supplierReference: newSupplierRef.trim() || undefined,
        receiptEvidence: newEvidence.trim() || undefined,
        status: newStatus,
      });
      setCreateOpen(false);
      resetCreate();
      push('بهر جدید ثبت شد.');
      result.reload();
    } catch (err: unknown) {
      setCreateError(bannerMessageFrom(err, 'ثبت بهر انجام نشد.'));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (e: FormEvent) => {
    e.preventDefault();
    if (!statusItem || updatingStatus) return;
    setUpdatingStatus(true);
    setStatusError(null);
    try {
      await apiPatch(`${base}/api/v1/inventory/lots/${statusItem.lot_id}/status`, {
        status: targetStatus,
      });
      setStatusItem(null);
      push('وضعیت بهر تغییر کرد.');
      result.reload();
    } catch (err: unknown) {
      setStatusError(bannerMessageFrom(err, 'تغییر وضعیت بهر انجام نشد.'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReleaseQuarantine = async (e: FormEvent) => {
    e.preventDefault();
    if (!quarantineItem || releasing) return;
    setReleasing(true);
    setQuarantineError(null);
    try {
      await apiPost(`${base}/api/v1/inventory/lots/${quarantineItem.lot_id}/quarantine-release`, {
        targetStatus: quarantineTarget,
        inspectionNotes: inspectionNotes.trim() || undefined,
      });
      setQuarantineItem(null);
      setInspectionNotes('');
      push(quarantineTarget === 'released' ? 'بهر از قرنطینه ترخیص شد.' : 'بهر مسدود شد.');
      result.reload();
    } catch (err: unknown) {
      setQuarantineError(bannerMessageFrom(err, 'ثبت نتیجهٔ بازرسی انجام نشد.'));
    } finally {
      setReleasing(false);
    }
  };

  const handleExpirePastDate = async () => {
    setExpiring(true);
    setPageError(null);
    try {
      const res = await apiPost<{ expiredCount: number }>(
        `${base}/api/v1/inventory/lots/expire-past-date`,
        {},
      );
      setExpireOpen(false);
      push(
        res.expiredCount > 0
          ? `${faDigits(res.expiredCount)} بهر منقضی‌شده شناسایی و به‌روز شد.`
          : 'بهر منقضی‌ای پیدا نشد.',
        res.expiredCount > 0 ? 'success' : 'info',
      );
      result.reload();
    } catch (err: unknown) {
      setExpireOpen(false);
      setPageError(bannerMessageFrom(err, 'اعمال انقضای خودکار انجام نشد.'));
    } finally {
      setExpiring(false);
    }
  };

  const columns: V2GridColumn<LotListItem>[] = [
    {
      key: 'lot_code',
      header: 'کد بهر',
      render: (r) => <span className="inv-code ltr">{r.lot_code}</span>,
    },
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      render: (r) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{r.product_name}</span>
          <span className="inv-cell__sub ltr">{r.product_code}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (r) => {
        const meta = metaOf(LOT_STATUS_META, r.status);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'dates',
      header: 'تولید / انقضا',
      render: (r) => (
        <span className="inv-cell">
          <span className="inv-cell__sub">
            تولید: {r.manufacture_date ? displayJalaliDate(r.manufacture_date) : '—'}
          </span>
          <span className="inv-cell__main">
            انقضا: {r.expiry_date ? displayJalaliDate(r.expiry_date) : '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'on_hand',
      header: 'موجودی',
      align: 'end',
      render: (r) => (
        <span className="sk-num">{formatQuantityParts(r.on_hand_qty_num, r.on_hand_qty_den)}</span>
      ),
    },
    {
      key: 'refs',
      header: 'مرجع تأمین / مستند',
      wrap: true,
      render: (r) => (
        <span className="inv-cell">
          <span className="inv-cell__sub">تأمین: {r.supplier_reference || '—'}</span>
          <span className="inv-cell__sub">مستند: {r.receipt_evidence || '—'}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (r) => (
        <span className="inv-actions">
          {r.status === 'quarantined' ? (
            <V2Button
              size="sm"
              variant="primary"
              onClick={() => {
                setQuarantineItem(r);
                setQuarantineTarget('released');
                setInspectionNotes('');
                setQuarantineError(null);
              }}
            >
              ترخیص بازرسی
            </V2Button>
          ) : null}
          <V2Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setStatusItem(r);
              setTargetStatus(r.status);
              setStatusError(null);
            }}
          >
            تغییر وضعیت
          </V2Button>
          <V2Button size="sm" variant="ghost" onClick={() => setDetailItem(r)}>
            موجودی در انبارها
          </V2Button>
        </span>
      ),
    },
  ];

  const balanceColumns: V2TableColumn<BalanceRow>[] = [
    {
      key: 'warehouse',
      header: 'انبار / محل',
      render: (b) => (
        <span className="inv-cell">
          <span className="inv-cell__main">
            {warehouseName.get(b.warehouseId) ?? 'انبار حذف‌شده یا نامشخص'}
          </span>
          <span className="inv-cell__sub">
            {locationName.get(b.warehouseLocationId) ?? 'بدون محل مشخص'}
          </span>
        </span>
      ),
    },
    {
      key: 'onHand',
      header: 'موجودی',
      align: 'end',
      render: (b) => <span className="sk-num">{formatQuantity(b.onHand)}</span>,
    },
    {
      key: 'reserved',
      header: 'رزرو',
      align: 'end',
      render: (b) => <span className="sk-num">{formatQuantity(b.reserved)}</span>,
    },
    {
      key: 'blocked',
      header: 'مسدود',
      align: 'end',
      render: (b) => <span className="sk-num">{formatQuantity(b.blocked)}</span>,
    },
  ];

  if (!slug) return null;

  const total = result.status === 'ready' ? result.data.total : 0;
  const productPicker = (
    <select
      className="sk-select inv-toolbar-select"
      aria-label="فیلتر کالا"
      value={productFilter}
      onChange={(e) => setProductFilter(e.target.value)}
    >
      <option value="">همهٔ کالاها</option>
      {productOptions.map((p) => (
        <option key={p.value} value={p.value}>
          {p.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="inv-page">
      {pageError ? (
        <V2Alert tone="danger" onClose={() => setPageError(null)}>
          {pageError}
        </V2Alert>
      ) : null}

      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            result.error.status === 403
              ? 'دسترسی مشاهدهٔ بهرها را ندارید'
              : 'بهرها قابل نمایش نیستند'
          }
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon="layers"
          title="بهرها"
          subtitle={`${faDigits(total)} بهر`}
          search={{ value: search, onChange: setSearch, placeholder: 'کد بهر یا نام کالا' }}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: statusFilter,
              options: STATUS_FILTER_OPTIONS,
              onChange: setStatusFilter,
            },
          ]}
          toolbarExtra={
            <>
              {productOptions.length > 0 ? productPicker : null}
              <V2Button
                variant="secondary"
                size="sm"
                icon="clock"
                onClick={() => setExpireOpen(true)}
              >
                اعمال انقضای خودکار
              </V2Button>
            </>
          }
          actions={
            <V2Button
              variant="primary"
              icon="plus"
              onClick={() => {
                resetCreate();
                setCreateOpen(true);
              }}
            >
              ثبت بهر جدید
            </V2Button>
          }
          columns={columns}
          rows={result.data.items}
          rowKey={(r) => r.lot_id}
          zebra
          storageKey="inventory.lots"
          emptyState={
            <V2EmptyState
              icon="layers"
              title="بهری پیدا نشد"
              description="بهری با این جست‌وجو یا فیلتر ثبت نشده است."
              compact
            />
          }
        />
      ) : null}

      <V2Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dismissible={!creating}
        title="ثبت بهر جدید"
        footer={
          <>
            <V2Button variant="ghost" disabled={creating} onClick={() => setCreateOpen(false)}>
              انصراف
            </V2Button>
            <V2Button type="submit" form="lot-create-form" variant="primary" disabled={creating}>
              {creating ? 'در حال ثبت…' : 'ثبت بهر'}
            </V2Button>
          </>
        }
      >
        <form id="lot-create-form" className="inv-form" noValidate onSubmit={handleCreateLot}>
          {createError ? <V2Alert tone="danger">{createError}</V2Alert> : null}
          <V2FormField
            label="کالا"
            required
            width="full"
            hint={
              productsTruncated
                ? `فقط ${faDigits(PICKER_LIMIT)} کالای اول فهرست نمایش داده می‌شود.`
                : undefined
            }
          >
            <select
              className="sk-select"
              value={newProductId}
              onChange={(e) => setNewProductId(e.target.value)}
            >
              <option value="">انتخاب کالا…</option>
              {productOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </V2FormField>
          <V2FormField label="کد بهر" required width="full">
            <input
              className="sk-input ltr"
              placeholder="مثلاً LOT-2026-001"
              value={newLotCode}
              onChange={(e) => setNewLotCode(e.target.value)}
            />
          </V2FormField>
          <div className="inv-form__row">
            <V2FormField label="تاریخ تولید" width="full">
              <JalaliDateInput value={newMfgDate} onChange={setNewMfgDate} />
            </V2FormField>
            <V2FormField label="تاریخ انقضا" width="full">
              <JalaliDateInput value={newExpiryDate} onChange={setNewExpiryDate} />
            </V2FormField>
          </div>
          <div className="inv-form__row">
            <V2FormField label="مرجع تأمین‌کننده" width="full">
              <input
                className="sk-input ltr"
                placeholder="مثلاً SUP-BATCH-892"
                value={newSupplierRef}
                onChange={(e) => setNewSupplierRef(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="وضعیت اولیه" width="full">
              <select
                className="sk-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {optionsOf(LOT_STATUS_META, NEW_LOT_STATUS_KEYS).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </V2FormField>
          </div>
          <V2FormField
            label="مستند آزمون یا رسید"
            width="full"
            hint="مثلاً شمارهٔ برگهٔ آنالیز کیفی"
          >
            <input
              className="sk-input"
              value={newEvidence}
              onChange={(e) => setNewEvidence(e.target.value)}
            />
          </V2FormField>
        </form>
      </V2Modal>

      <V2Modal
        open={statusItem !== null}
        onClose={() => setStatusItem(null)}
        dismissible={!updatingStatus}
        size="sm"
        title="تغییر وضعیت بهر"
        footer={
          <>
            <V2Button variant="ghost" disabled={updatingStatus} onClick={() => setStatusItem(null)}>
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="lot-status-form"
              variant="primary"
              disabled={updatingStatus || targetStatus === statusItem?.status}
            >
              {updatingStatus ? 'در حال ثبت…' : 'ثبت وضعیت'}
            </V2Button>
          </>
        }
      >
        <form id="lot-status-form" className="inv-form" noValidate onSubmit={handleUpdateStatus}>
          {statusItem ? (
            <p className="inv-form__lead">
              بهر <span className="ltr">{statusItem.lot_code}</span> — {statusItem.product_name}
            </p>
          ) : null}
          {statusError ? <V2Alert tone="danger">{statusError}</V2Alert> : null}
          <V2FormField label="وضعیت جدید" width="full">
            <select
              className="sk-select"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
            >
              {optionsOf(LOT_STATUS_META).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </V2FormField>
        </form>
      </V2Modal>

      <V2Modal
        open={quarantineItem !== null}
        onClose={() => setQuarantineItem(null)}
        dismissible={!releasing}
        title="ترخیص از قرنطینه"
        footer={
          <>
            <V2Button variant="ghost" disabled={releasing} onClick={() => setQuarantineItem(null)}>
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="lot-quarantine-form"
              variant={quarantineTarget === 'blocked' ? 'danger' : 'primary'}
              disabled={releasing}
            >
              {releasing ? 'در حال ثبت…' : 'ثبت نتیجهٔ بازرسی'}
            </V2Button>
          </>
        }
      >
        <form
          id="lot-quarantine-form"
          className="inv-form"
          noValidate
          onSubmit={handleReleaseQuarantine}
        >
          {quarantineItem ? (
            <p className="inv-form__lead">
              بهر <span className="ltr">{quarantineItem.lot_code}</span> —{' '}
              {quarantineItem.product_name}
            </p>
          ) : null}
          {quarantineError ? <V2Alert tone="danger">{quarantineError}</V2Alert> : null}
          <V2FormField label="نتیجهٔ بازرسی" required width="full">
            <select
              className="sk-select"
              value={quarantineTarget}
              onChange={(e) => setQuarantineTarget(e.target.value as 'released' | 'blocked')}
            >
              <option value="released">تأیید کیفی؛ ترخیص برای فروش و مصرف</option>
              <option value="blocked">عدم انطباق؛ مسدودسازی دائم</option>
            </select>
          </V2FormField>
          <V2FormField
            label="شرح بازرسی"
            width="full"
            hint="نتایج آزمایشگاه، شمارهٔ برگهٔ آنالیز کیفی، درصد خلوص…"
          >
            <textarea
              className="sk-input"
              rows={3}
              value={inspectionNotes}
              onChange={(e) => setInspectionNotes(e.target.value)}
            />
          </V2FormField>
        </form>
      </V2Modal>

      <V2Modal
        open={detailItem !== null}
        onClose={() => setDetailItem(null)}
        size="lg"
        title={detailItem ? `موجودی بهر ${detailItem.lot_code}` : 'موجودی بهر'}
        footer={
          <V2Button variant="secondary" onClick={() => setDetailItem(null)}>
            بستن
          </V2Button>
        }
      >
        {detailItem ? (
          <p className="inv-form__lead">
            {detailItem.product_name} · وضعیت: {metaOf(LOT_STATUS_META, detailItem.status).label} ·
            انقضا: {detailItem.expiry_date ? displayJalaliDate(detailItem.expiry_date) : 'ثبت نشده'}
          </p>
        ) : null}
        {detail.status === 'loading' ? <LoadingState /> : null}
        {detail.status === 'error' ? (
          <StatePanel
            variant={detail.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={detail.error.status === 403 ? 'دسترسی ندارید' : 'موجودی بهر قابل نمایش نیست'}
          />
        ) : null}
        {detail.status === 'ready' ? (
          <V2Table
            columns={balanceColumns}
            rows={detail.data.balances.map((b, i) => ({
              ...b,
              id: `${b.warehouseId}:${b.warehouseLocationId}:${i}`,
            }))}
            emptyState={
              <V2EmptyState
                icon="inventory"
                title="موجودی ثبت نشده است"
                description="این بهر هنوز در هیچ انباری موجودی ندارد."
                compact
              />
            }
          />
        ) : null}
      </V2Modal>

      <V2ConfirmDialog
        open={expireOpen}
        title="اعمال انقضای خودکار"
        message="وضعیت همهٔ بهرهایی که تاریخ انقضایشان گذشته است به «منقضی‌شده» تغییر می‌کند و از موجودی قابل استفاده کسر می‌شود. ادامه می‌دهید؟"
        confirmLabel="اعمال انقضا"
        loading={expiring}
        onConfirm={() => void handleExpirePastDate()}
        onCancel={() => setExpireOpen(false)}
      />

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
